import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wind, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const SIDE = 'https://images.pexels.com/photos/15969122/pexels-photo-15969122/free-photo-of-kitesurfers-in-sea.jpeg?auto=compress&cs=tinysrgb&w=1280';

export default function Login() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(t('auth.login.welcomeToast', { name: u.name }));
      const dest = sessionStorage.getItem('kiteiq_pending_plan')
        ? '/pricing'
        : (location.state?.from || (u.onboarded ? '/dashboard' : '/onboarding'));
      navigate(dest);
    } catch (err) {
      toast.error(apiError(err, t('auth.login.failed')));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={SIDE} alt="Kitesurfing" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0.4), rgba(10,10,10,0.85))' }} />
        <div className="absolute bottom-10 left-10 right-10">
          <h2 className="font-display text-5xl">{t('auth.login.sideTitle1')}<br/><span className="text-gradient-blue">{t('auth.login.sideTitle2')}</span></h2>
          <p className="text-white/70 mt-3 max-w-sm">{t('auth.login.sideSub')}</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8"><Wind className="w-7 h-7 text-[#00B4D8]" /><span className="font-display text-2xl">KITEIQ</span></Link>
          <h1 className="text-2xl font-semibold">{t('auth.login.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('auth.login.subtitle')}</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">{t('auth.login.email')}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.login.emailPlaceholder')} className="mt-1.5 bg-[#12161B] border-white/15" data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t('auth.login.password')}</Label>
                <Link to="/forgot-password" className="text-xs text-[#48CAE4] hover:text-[#00B4D8] font-medium" data-testid="login-forgot-link">
                  {t('auth.login.forgotLink')}
                </Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="mt-1.5 bg-[#12161B] border-white/15" data-testid="login-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="login-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('auth.login.submit')}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            {t('auth.login.noAccount')} <Link to="/register" className="text-[#48CAE4] hover:text-[#00B4D8] font-medium">{t('auth.login.signupFree')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
